import React, { useContext } from 'react';

import { useHistory, useLocation } from 'react-router';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import { sortBy, keyBy, intersection, uniq } from 'lodash';
import dayjs from 'dayjs';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faEdit, faSearch, faTimes } from '@fortawesome/pro-light-svg-icons';
import { Button, Popover, Tabs, Tag, Tooltip, Layout, Flex } from 'antd';

import { useRiskController } from 'hooks/useRiskController';
import { getItemAlteredByScenario, isRiskOrOpportunityImpactBetter, isRiskOrOpportunityImpactWorse, isRiskOrOpportunityLikelihoodBetter, isRiskOrOpportunityLikelihoodWorse } from 'utils/rules';

import AddButton from 'components/action/AddButton';
import RiskEditionModal from 'components/Modal/RiskEditionModal';
import StakeEditionModal from 'components/Modal/StakeEditionModal';
import { ConfirmModal } from 'components/Modal/ConfirmModal';

import { RiskScatterFullChart } from 'components/charts/RiskScatterFullChart';
import Table from 'components/Table';
import RiskPopover from './RiskPopover';
import RiskTooltipContent from './RiskTooltipContent';

import { StakeholderModel } from 'model/StakeholderModel';
import { StakeholderRiskImpactModel } from 'model/StakeholderRiskImpactModel';
import { RiskModel } from 'model/RiskModel';

import { FiltersTab } from './Tabs/FiltersTab';
import { OptionsTab } from './Tabs/OptionsTab';
import { RiskGeneralTab } from './Tabs/RiskGeneralTab';
import { StakeModel } from 'model/StakeModel';

import { StakeholderImpactsTab } from 'components/tabs/StakeholderImpactsTab';
import { FishboneLinksTab } from 'components/tabs/FishboneLinksTab';
import { useStakeholderController } from 'hooks/useStakeholderController';
import { useStakeController } from 'hooks/useStakeController';
import { StakeGeneralTab } from '../Stakes/Tabs/StakeGeneralTab';
import { useAuth } from 'auth';
import BotTab from 'components/tabs/BotTab';
import DisabledTab from 'components/tabs/DisabledTab';
import GeneralTab from '../Navigator/Tabs/GeneralTab';

import UIContext from 'components/Utils/UIContext';
import { SideMenu } from 'layout/SideMenu';
import { ExportTab } from './Tabs/ExportTab';
import SubHeaderPortal from 'layout/header/SubHeaderPortal';
import { useOnboardingStep } from 'components/Onboarding/OnboardingReviewBanner';
import Tutorial from 'components/Tutorial/Tutorial';
import { MaskableComponent } from 'components/Tutorial/MaskableComponent';
import CommentsTab from '../Navigator/Tabs/CommentsTab';
import { ItemTagsTab } from 'components/tabs/ItemTagsTab';

import './Risks.sass';


type FilterObject = {
	name: string,
	riskFilter: string,
	categoryFilter: string[] | null[],
	impactFilter: number[],
	likelihoodFilter: number[],
	tagFilter: string[],
	stakeholderFilter: string[],
	stakeFilter: string[],
	indexedStakeholdersWithRisks: {
		[key: string]: string[]
	},
}

const selectableElementTypes = ['Risk', 'Stake', 'Stakeholder']

const filterRisks = (isRiskMode: boolean, risks: any[], filters: FilterObject) => {

	let limitedRisks = risks.filter(r => (!!r.isOpportunity) != isRiskMode)
	limitedRisks = limitedRisks.map((r, index) => ({ ...r, index }))

	if (filters && filters.riskFilter) {
		limitedRisks = limitedRisks.filter(r => r.id == filters.riskFilter)
	}

	if (filters && filters.categoryFilter && filters.categoryFilter.length > 0) {
		limitedRisks = limitedRisks.filter(r => filters.categoryFilter.includes(r.riskCategoryId) || (!r.riskCategoryId && filters.categoryFilter.includes(null)))
	}

	if (filters && filters.impactFilter && filters.impactFilter.length > 0) {
		limitedRisks = limitedRisks.filter(r => filters.impactFilter.includes("" + parseInt(r.impact)))
	}

	if (filters && filters.likelihoodFilter && filters.likelihoodFilter.length > 0) {
		limitedRisks = limitedRisks.filter(r => filters.likelihoodFilter.includes("" + parseInt(r.likelihood)))
	}

	if (filters && filters.tagFilter && filters.tagFilter.length > 0) {
		limitedRisks = limitedRisks.filter(r => r.taggings && r.taggings.length > 0)
		// Keep only risks that have at least one of the tags
		limitedRisks = limitedRisks.filter(r => intersection(r.taggings.map(t => t.tagId), filters.tagFilter).length > 0)

	}

	if (filters && filters.stakeFilter && filters.stakeFilter.length > 0) {
		// Keep only risks that have at least one of the stakes
		filters.stakeFilter.forEach((i: string) => {
			limitedRisks = limitedRisks.filter(r => {
				return r.impactedStakes.map(ri => ri.stakeId).includes(i)
			})
		})
	}

	if (filters && filters.stakeholderFilter && filters.stakeholderFilter.length > 0) {
		let allRisksIds: string[] = []
		filters.stakeholderFilter.forEach((s: string) => {
			if (filters.indexedStakeholdersWithRisks[s] && filters.indexedStakeholdersWithRisks[s].length > 0) {
				allRisksIds = allRisksIds.concat(filters.indexedStakeholdersWithRisks[s])
			}
		})
		// Keep only risks that have at least one of the stakeholders
		limitedRisks = limitedRisks.filter(r => {
			return allRisksIds.includes(r.id)
		})
	}

	if (filters && filters.name) {
		limitedRisks = limitedRisks.filter(r => {
			return r.name.toLowerCase().includes(filters.name.toLowerCase())
		})
	}

	return limitedRisks
}

const defaultValuesBasedOnFilters = (filters: FilterObject) => {

	let defaults = {}

	//console.log('defaultValuesBasedOnFilters', filters)

	// Apply new values based on filters
	if (filters && filters.categoryFilter && filters.categoryFilter.length > 0) {
		defaults.riskCategoryId = filters.categoryFilter[0]
	}

	if (filters && filters.impactFilter && filters.impactFilter.length > 0) {
		defaults.impact = filters.impactFilter[0]
	}

	if (filters && filters.likelihoodFilter && filters.likelihoodFilter.length > 0) {
		defaults.likelihood = filters.likelihoodFilter[0]
	}

	if (filters && filters.tagFilter && filters.tagFilter.length > 0) {
		defaults.taggings = filters.tagFilter
	}

	return defaults
}


export const Risks = (props: any) => {

	const { user } = useAuth();

	let history = useHistory();
	const params: any = useParams();
	const location = useLocation();
	const isRiskMode = location.pathname.includes('/risques') || location.pathname.endsWith('/risks');
	const isSummaryMode = location.pathname.includes('summary');
	const uiContext = useContext(UIContext);

	const { risks = [], stakeholders = [], stakes = [], likelihoods = [], impacts = [], tags = [], riskCategories = [], scenarios = [] } = props;

	const selection = props.selection?.__typename === "Risk" ? risks.find((r: any) => r.id === props.selection?.id) : props.selection?.__typename === "Stake" ? stakes.find((r: any) => r.id === props.selection?.id) : props.selection?.__typename === "Stakeholder" ? stakeholders.find((r: any) => r.id === props.selection?.id) : null;

	const [editedItem, setEditedItem] = React.useState<any | null>(null);
	const [openPopover, setOpenPopover] = React.useState<string | null>(null);

	// Largeur réelle du conteneur de la viz : le chart est paramétrique sur sa
	// largeur, on lui passe la place disponible (cappée à 1200) plutôt qu'une
	// valeur fixe qui se faisait couper quand le panneau de filtres réduit la
	// largeur. Callback ref + ResizeObserver pour survivre au montage/démontage
	// sur changement d'onglet.
	const [vizWidth, setVizWidth] = React.useState<number>(1200);
	// `vizResizing` est levé le temps du resize pour couper la transition de
	// repositionnement des points (animation inutile au resize). Piloté par
	// l'observer (événement, pas diff de rendu) → correct sous StrictMode.
	const [vizResizing, setVizResizing] = React.useState<boolean>(false);
	const vizResizeObserverRef = React.useRef<ResizeObserver | null>(null);
	const vizResizeTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
	// Dernière largeur appliquée. Garde anti-boucle : le rendu du chart peut
	// faire re-notifier l'observer (et `Math.round` oscille au sub-pixel, ex.
	// 1199↔1200), ce qui, sans garde, déclenchait un setState à chaque
	// notification → re-render perpétuel → la page /risks ne se stabilisait
	// jamais (clics sur les icônes de ligne en timeout, jank). On n'actualise
	// que sur un vrai changement (seuil 2 px).
	const vizWidthRef = React.useRef<number>(1200);
	const vizContainerRef = React.useCallback((node: HTMLDivElement | null) => {
		if (vizResizeObserverRef.current) {
			vizResizeObserverRef.current.disconnect();
			vizResizeObserverRef.current = null;
		}
		if (node && typeof ResizeObserver !== 'undefined') {
			const ro = new ResizeObserver((entries) => {
				const w = entries[0]?.contentRect?.width;
				if (!w) return;
				const next = Math.min(Math.round(w), 1200);
				// Ignore les notifications no-op / oscillations sub-pixel : sinon
				// boucle de resize et page jamais stable.
				if (Math.abs(next - vizWidthRef.current) < 2) return;
				vizWidthRef.current = next;
				// Sur un vrai changement de largeur : on lève `resizing` le temps du
				// recalage pour couper la transition transform 1.8s (sinon les
				// éléments glisseraient). Calage initial (1200 → largeur réelle)
				// inclus.
				setVizResizing(true);
				setVizWidth(next);
				if (vizResizeTimerRef.current) clearTimeout(vizResizeTimerRef.current);
				vizResizeTimerRef.current = setTimeout(() => setVizResizing(false), 150);
			});
			ro.observe(node);
			vizResizeObserverRef.current = ro;
		}
	}, []);

	// Fermer le popover quand on clique ailleurs
	React.useEffect(() => {
		if (!openPopover) return;

		const handleClickOutside = (e: MouseEvent) => {
			const target = e.target as HTMLElement;
			// Ne pas fermer si on clique dans le popover
			if (target.closest('.ant-popover')) return;
			setOpenPopover(null);
		};

		// Délai pour ne pas capturer le clic d'ouverture
		const timeoutId = setTimeout(() => {
			document.addEventListener('click', handleClickOutside);
		}, 50);

		return () => {
			clearTimeout(timeoutId);
			document.removeEventListener('click', handleClickOutside);
		};
	}, [openPopover]);

	const { createRisk, updateRisk, deleteRisk, updateRiskTaggings, createStakeImpact, deleteStakeImpact, updateStakeImpactStrength, cycleOpeningLink } = useRiskController();
	const { createRiskImpact, updateRiskImpactStrength, deleteRiskImpact } = useStakeholderController();
	const { updateStake } = useStakeController();
	const indexedStakeholders = keyBy(stakeholders, 'id');

	const { t } = useTranslation();

	const onboardingRisks = useOnboardingStep('risks');
	const onboardingOpportunities = useOnboardingStep('opportunities');
	const onboardingActive = onboardingRisks.active || onboardingOpportunities.active;

	const isEvaluatedAtVisible = props.preferences?.pages?.risks?.evaluatedAt !== undefined ? props.preferences?.pages?.risks?.evaluatedAt : true
	const isRiskCategoryVisible = props.preferences?.pages?.risks?.riskCategory !== undefined ? props.preferences?.pages?.risks?.riskCategory : true
	const isStakeholdersVisible = props.preferences?.pages?.risks?.stakeholders !== undefined ? props.preferences?.pages?.risks?.stakeholders : false
	const isMatchingROVisible = props.preferences?.pages?.risks?.matchingRO !== undefined ? props.preferences?.pages?.risks?.matchingRO : false
	const isDescriptionVisible = props.preferences?.pages?.risks?.description !== undefined ? props.preferences?.pages?.risks?.description : true
	const areLinksVisible = props.preferences?.pages?.risks?.allLinks !== undefined ? props.preferences?.pages?.risks?.allLinks : false
	const showStakes = props.preferences?.pages?.risks?.stakesChart !== undefined ? props.preferences?.pages?.risks?.stakesChart : true
	const showRisks = props.preferences?.pages?.risks?.risksChart !== undefined ? props.preferences?.pages?.risks?.risksChart : true
	const showOpportunities = props.preferences?.pages?.risks?.opportunitiesChart !== undefined ? props.preferences?.pages?.risks?.opportunitiesChart : true
	const showStakeholders = props.preferences?.pages?.risks?.stakeholdersChart !== undefined ? props.preferences?.pages?.risks?.stakeholdersChart : true

	let impactedStakeholders = stakeholders.filter(
		(s) => s.impactedRisks.some((ir) => ir.riskId === selection?.id)
	);

	//console.log('Risks screen', props)

	const unfilteredRisks = (risks.map(r => getItemAlteredByScenario(r, props.scenario))).filter(r => r.enabled !== false);
	const alteredRisks = unfilteredRisks.filter(r => isSummaryMode || isRiskMode == !r.isOpportunity);

	// Filters - Categories
	const allCategories = ([{ id: null, name: "Unassigned" }]).concat(sortBy(riskCategories, "name"));
	const [categoryFilter, setCategoryFilter] = React.useState<string[]>([]);

	// Filters - Impacts
	const [impactFilter, setImpactFilter] = React.useState<number[]>([]);

	// Filters - Likelihoods
	const [likelihoodFilter, setLikelihoodFilter] = React.useState<number[]>([]);

	// Filters - Tags
	let usedTags = uniq(alteredRisks.map((r: RiskModel) => r.taggings.map((t: any) => t.tagId)).flat());
	const allTags: any = sortBy(tags, 'name');

	const [tagFilter, setTagFilter] = React.useState<string[]>([]);

	// Filters - Stakeholders
	const indexedStakeholdersWithRisks = {};
	const groupedStakeholdersByRisk = {};
	const indexedRisks = keyBy((alteredRisks || []), 'id');

	stakeholders.forEach((s: StakeholderModel) => {
		s.impactedRisks.filter(r => isRiskMode !== r.isOpportunity).forEach((ir: StakeholderRiskImpactModel) => {

			if (indexedRisks[ir.riskId]) {

				if (!indexedStakeholdersWithRisks[s.id]) {
					indexedStakeholdersWithRisks[s.id] = []
				}

				if (!groupedStakeholdersByRisk[ir.riskId]) {
					groupedStakeholdersByRisk[ir.riskId] = []
				}

				groupedStakeholdersByRisk[ir.riskId].push(s)
				indexedStakeholdersWithRisks[s.id].push(ir.riskId)
			}
		})
	})
	const allStakeholders: StakeholderModel[] = sortBy(stakeholders, 'name').filter((s: StakeholderModel) => !!indexedStakeholdersWithRisks[s.id]);
	const [stakeholderFilter, setStakeholderFilter] = React.useState<string[]>([]);

	// Filters - Stakes
	const indexedStakesWithRisks = {};
	const groupedStakesByRisk = {};

	risks.forEach((r: RiskModel) => {
		r.impactedStakes.forEach((ii: StakeholderRiskImpactModel) => {

			if (indexedRisks[ii.riskId]) {

				if (!indexedStakesWithRisks[ii.stakeId]) {
					indexedStakesWithRisks[ii.stakeId] = []
				}

				if (!groupedStakesByRisk[ii.riskId]) {
					groupedStakesByRisk[ii.riskId] = []
				}

				groupedStakesByRisk[ii.riskId].push(r)
				indexedStakesWithRisks[ii.stakeId].push(ii.riskId)
			}
		})
	})
	const allStakes: StakeModel[] = sortBy(stakes, 'name').filter((i: StakeModel) => !!indexedStakesWithRisks[i.id]);
	const [stakeFilter, setStakeFilter] = React.useState<string[]>([]);


	const getAggregatedFilters = (): FilterObject => {
		return {
			name: props.searchTerm,
			riskFilter: location?.state?.riskFilter,
			categoryFilter: categoryFilter,
			impactFilter: impactFilter,
			likelihoodFilter: likelihoodFilter,
			stakeholderFilter: stakeholderFilter,
			stakeFilter: stakeFilter,
			indexedStakeholdersWithRisks,
			tagFilter: tagFilter
		}
	}

	const filteredElements = filterRisks(isRiskMode, alteredRisks, getAggregatedFilters())
	const filteredRisks = filterRisks(true, unfilteredRisks, getAggregatedFilters())
	const filteredOpportunities = filterRisks(false, unfilteredRisks, getAggregatedFilters())

	// Risques désactivés filtrés selon le mode (risques ou opportunités)
	const disabledRisksForTab = risks.filter(r => isRiskMode ? !r.isOpportunity : !!r.isOpportunity);

	const indexedLikelihoods = likelihoods ? keyBy(likelihoods, "id") : {}
	const indexedImpacts = impacts ? keyBy(impacts, "id") : {}


	const goToNavigatorWithRiskFilter = (riskId: string) => {
		history.push('/projects/' + params.id, {
			riskFilter: riskId
		});
	}

	const goToRisks = (mode: string, riskId: string) => {
		let allFilters = {}
		if (riskId) allFilters = { riskFilter: riskId }
		props.deselect();

		history.push('/projects/' + params.id + '/' + mode, allFilters);
	}

	// Create
	// `overrides` permet de forcer isOpportunity et/ou une position (impact /
	// likelihood) selon le contexte d'appel : le bouton + de la liste utilise le
	// mode courant (isRiskMode), tandis que la création depuis la viz force la
	// zone (Risques / Ouvertures) et peut pré-renseigner la position cliquée.
	const handleCreateRisk = async (overrides: { isOpportunity?: boolean; impact?: number; likelihood?: number } = {}) => {
		props.pushModal(
			<RiskEditionModal
				onHide={() => {
					props.popModal();
				}}
				onSave={async (values: any) => {
					await createRisk(params.id, risks, values, props.scenario, {}, props.scenarios || []);
					props.popModal();
				}}
				tags={tags}
				id={false}
				values={{
					...defaultValuesBasedOnFilters(getAggregatedFilters()),
					isOpportunity: overrides.isOpportunity !== undefined ? overrides.isOpportunity : (isRiskMode ? false : true),
					...(overrides.impact !== undefined ? { impact: overrides.impact } : {}),
					...(overrides.likelihood !== undefined ? { likelihood: overrides.likelihood } : {}),
					impactedStakes: [],
					riskIds: [],
					evaluatedAt: null
				}}
				riskCategories={riskCategories}
				likelihoods={likelihoods || []}
				impacts={impacts || []}
				risks={risks}
				stakes={stakes}
			></RiskEditionModal>
		);
	};

	const askDeleteConfirmation = async (id?: string, isOpportunity?: boolean) => {
		const prefix = isOpportunity ? 'models.risk.messages.delete_opening' : 'models.risk.messages.delete_confirmation';
		props.pushModal(
			<ConfirmModal
				title={t(prefix)}
				t={t}
				onConfirm={() => deleteRisk(params.id, id, risks)}
				message={t(`${prefix}_explanation`)}
			></ConfirmModal>
		);
	};

	const columns = [

		uiContext.canEdit ? {
			title: '',
			dataIndex: 'edit',
			key: 'edit',
			width: 50,
			render: (text, record) => (
				<Tooltip title='Edit'>
					<Button
						shape="circle"
						onClick={() => setEditedItem(record)}
					>
						<FontAwesomeIcon icon={faEdit} />
					</Button>
				</Tooltip>
			)
		} : null,
		{
			title: '#',
			dataIndex: 'id',
			key: 'id',
			defaultSortOrder: 'ascend',
			width: 60,
			sortable: (a, b) => {
				const scoreA = (a.reference || (a.isOpportunity ? "O" : "R") + ((a.index + 1 + "").padStart(2, "0"))).toLocaleLowerCase()
				const scoreB = (b.reference || (b.isOpportunity ? "O" : "R") + ((b.index + 1 + "").padStart(2, "0"))).toLocaleLowerCase()

				if (scoreA < scoreB) return -1;
				if (scoreA > scoreB) return 1;
				return 0
			},
			render: (text, record, index) => {

				return record.reference || (record.isOpportunity ? "O" : "R") + ((record.index + 1 + "").padStart(2, "0"))
			}
		},
		isEvaluatedAtVisible && {
			title: t("models.risk.fields.evaluated_at"),
			dataIndex: 'evaluatedAt',
			key: 'evaluatedAt',
			width: 125,
			className: 'nowrap',
			sortable: (a, b) => {
				if (!b.evaluatedAt) return -1
				if (!a.evaluatedAt) return 1
				if (a.evaluatedAt && b.evaluatedAt && a.evaluatedAt < b.evaluatedAt) return -1;
				if (a.evaluatedAt && b.evaluatedAt > b.evaluatedAt) return 1;
				return 0
			},
			render: (text, record, index) => {

				return <Tooltip title={record.evaluatedAt ? dayjs(record.evaluatedAt).format('DD/MM/YYYY HH[h]mm') : ''}>
					<div>
						{record.evaluatedAt ? dayjs(record.evaluatedAt).format('DD/MM/YYYY') : ''}
					</div>
				</Tooltip>
				return record.evaluatedAt ? dayjs(record.evaluatedAt).format('DD/MM/YYYY') : ''
			}
		},
		{
			title: t("models.risk.fields.name"),
			dataIndex: 'name',
			key: 'name',
			className: 'w-100',
			sortable: true,
			render: (text: string, record) => {
				return (
					<><span className={[
						"name cursor-pointer",
						record.id === selection?.id ? "selected" : ""
					].join(" ")}
						onClick={() => record.id === selection?.id ? props.deselect() : props.select(record)}>
						{record.name}

					</span>
						{record.description && isDescriptionVisible ? (
							<div onClick={() => props.select(record)} className="description">
								{record.description}
							</div>
						) : (
							''
						)}</>
				);
			}
		},
		isRiskCategoryVisible && {
			title: t('models.risk.fields.risk_category'),
			dataIndex: 'riskCategoryId',
			key: 'riskCategoryId',
			sortable: (a: object, b: object) => {
				const valueForA = a.riskCategoryId ? riskCategories.find(c => c.id === a.riskCategoryId) : null;
				const valueForB = b.riskCategoryId ? riskCategories.find(c => c.id === b.riskCategoryId) : null;

				if (!valueForA && !valueForB) return 0
				if (!valueForA) return -1
				if (!valueForB) return 1

				if (valueForA.name < valueForB.name) return -1;
				if (valueForA.name > valueForB.name) return 1;
				return 0
			},
			width: 150,
			render: (text: string, record) => {
				const category = riskCategories.find(
					(c: any) => c.id === record.riskCategoryId
				);
				const popoverKey = `category-${record.id}`;
				if (!uiContext.canEdit) {
					return category ? category.name : <span style={{ opacity: 0.4 }}>{'—'}</span>;
				}
				// Clic -> popover de réassignation de catégorie (même style que
				// les popovers impact/probabilité). Pierre.
				return (
					<Popover
						trigger="click"
						className="cursor-pointer"
						open={openPopover === popoverKey}
						destroyTooltipOnHide
						onOpenChange={(visible) => {
							setOpenPopover(visible ? popoverKey : null);
						}}
						content={
							<div className="RiskPopover" style={{ maxWidth: 420 }}>
								{sortBy(riskCategories, 'name').map((c: any) => (
									<Tag
										key={c.id}
										className={c.id === record.riskCategoryId ? 'selected' : ''}
										onClick={() => {
											updateRisk(record.id, risks, props.scenario, { riskCategoryId: c.id });
											setOpenPopover(null);
										}}
									>
										{c.name}
									</Tag>
								))}
								<Tag
									className={!record.riskCategoryId ? 'selected' : ''}
									onClick={() => {
										updateRisk(record.id, risks, props.scenario, { riskCategoryId: null });
										setOpenPopover(null);
									}}
								>
									{t('models.risk_category.messages.no_x')}
								</Tag>
							</div>
						}
					>
						<span className="cursor-pointer">
							{category ? category.name : <span style={{ opacity: 0.4 }}>{'—'}</span>}
						</span>
					</Popover>
				);
			}
		},
		{
			title: t("models.risk.fields.impact"),
			dataIndex: 'impact',
			key: 'impact',
			sortable: true,
			width: 90,
			render: (text: string, record) => {

				let isBetter = isRiskOrOpportunityImpactBetter(record);
				let isWorse = isRiskOrOpportunityImpactWorse(record);
				const popoverKey = `impact-${record.id}`;

				if (!uiContext.canEdit) {
					return (
						<Tooltip
							styles={{ root: { minWidth: '400px' } }}
							title={<RiskTooltipContent
								risk={record}
								attribute={"impact"}
								scenario={props.scenario}
								scenarios={scenarios}
								impacts={impacts}
							></RiskTooltipContent>}>
							<div className={[isBetter ? "positive" : "", isWorse ? "negative" : ""].join(" ")}>
								{record.impact}
							</div>
						</Tooltip>
					);
				}

				return <Popover
					trigger="click"
					styles={{ root: { minWidth: '400px' } }}
					className='cursor-pointer'
					open={openPopover === popoverKey}
					destroyTooltipOnHide
					onOpenChange={(visible) => {
						// Ouvrir le popover uniquement, ne jamais le fermer via onOpenChange
						if (visible) {
							setOpenPopover(popoverKey);
						}
					}}
					content={
						<RiskPopover
							onChange={(value) => {
								updateRisk(
									record.id,
									risks,
									props.scenario,
									{ impact: value }
								);
								setOpenPopover(null);
							}}
							value={record.impact}
							values={impacts}
						/>
					}>
					<Tooltip
						styles={{ root: { minWidth: '400px' } }}
						open={openPopover === popoverKey ? false : undefined}
						title={<RiskTooltipContent
							risk={record}
							attribute={"impact"}
							scenario={props.scenario}
							scenarios={scenarios}
							impacts={impacts}
						></RiskTooltipContent>}>
						<div className={[isBetter ? "positive" : "", isWorse ? "negative" : ""].join(" ")}>
							{record.impact}
						</div>
					</Tooltip>
				</Popover>
			}
		},
		{
			title: t("models.risk.fields.likelihood"),
			dataIndex: 'likelihood',
			key: 'Likelihood',
			sortable: true,
			width: 105,
			render: (text: string, record) => {

				let isBetter = isRiskOrOpportunityLikelihoodBetter(record);
				let isWorse = isRiskOrOpportunityLikelihoodWorse(record);
				const popoverKey = `likelihood-${record.id}`;

				if (!uiContext.canEdit) {
					return (
						<Tooltip
							styles={{ root: { minWidth: '400px' } }}
							title={
								<RiskTooltipContent
									risk={record}
									attribute={"likelihood"}
									scenario={props.scenario}
									scenarios={scenarios}
									likelihoods={likelihoods}
								></RiskTooltipContent>
							}>
							<div className={[isBetter ? "positive" : "", isWorse ? "negative" : ""].join(" ")}>
								{record.likelihood}
							</div>
						</Tooltip>
					);
				}

				return <Popover
					trigger="click"
					className='cursor-pointer'
					styles={{ root: { minWidth: '400px' } }}
					open={openPopover === popoverKey}
					destroyTooltipOnHide
					onOpenChange={(visible) => {
						// Ouvrir le popover uniquement, ne jamais le fermer via onOpenChange
						if (visible) {
							setOpenPopover(popoverKey);
						}
					}}
					content={
						<RiskPopover
							onChange={(value) => {
								updateRisk(
									record.id,
									risks,
									props.scenario,
									{ likelihood: value }
								);
								setOpenPopover(null);
							}}
							value={record.likelihood}
							values={likelihoods}
						/>
					}>
					<Tooltip
						styles={{ root: { minWidth: '400px' } }}
						open={openPopover === popoverKey ? false : undefined}
						title={
							<RiskTooltipContent
								risk={record}
								attribute={"likelihood"}
								scenario={props.scenario}
								scenarios={scenarios}
								likelihoods={likelihoods}
							></RiskTooltipContent>
						}>
						<div className={[isBetter ? "positive" : "", isWorse ? "negative" : ""].join(" ")}>
							{record.likelihood}
						</div>
					</Tooltip>
				</Popover>
			}
		},
		,
		{
			title: isRiskMode ? t("models.risk.fields.exposure") : t("models.risk.fields.exposure_positive"),
			dataIndex: 'exposure',
			key: 'exposure',
			width: 105,
			sortable: (a: object, b: object) => {
				const exposureA = a.likelihood * a.impact
				const exposureB = b.likelihood * b.impact

				if (exposureA < exposureB) return -1;
				if (exposureA > exposureB) return 1;
				return 0
			},
			render: (text: string, record) => {

				let isBetter = false;
				let isWorse = false;
				let lessIsBetter = isRiskMode
				let baselineExposure = record?.o?.likelihood * record?.o?.impact
				let scenarioExposure = record?.likelihood * record?.impact
				if (baselineExposure > scenarioExposure) {
					if (lessIsBetter)
						isBetter = true
					else
						isWorse = true
				}
				if (baselineExposure < scenarioExposure) {
					if (lessIsBetter)
						isWorse = true
					else
						isBetter = true
				}

				return <Tooltip
					styles={{ root: { minWidth: '400px' } }}
					title={
						<RiskTooltipContent
							risk={record}
							attribute={"exposure"}
							scenario={props.scenario}
							scenarios={scenarios}
							likelihoods={likelihoods}
						></RiskTooltipContent>
					}>
					<div className={[isBetter ? "positive" : "", isWorse ? "negative" : ""].join(" ")}>
						{record.likelihood * record.impact}
					</div>
				</Tooltip>
			}
		},
		isStakeholdersVisible && {
			title: t("models.item.name_plural"),
			dataIndex: 'Stakeholders',
			key: 'Stakeholders',
			width: 120,
			sortable: (a: object, b: object) => {
				const scoreA = groupedStakeholdersByRisk[a.id]?.length || 0
				const scoreB = groupedStakeholdersByRisk[b.id]?.length || 0

				if (scoreA < scoreB) return -1;
				if (scoreA > scoreB) return 1;
				return 0
			},
			render: (text: string, record) => {
				return groupedStakeholdersByRisk[record.id]?.length > 0 && <>
					{groupedStakeholdersByRisk[record.id]?.length || 0}
					<Tooltip title={'View stakeholders'}>
						<Button
							shape="circle"
							className='ml-1'
							onClick={() => goToNavigatorWithRiskFilter(record.id)}
						>
							<FontAwesomeIcon
								icon={faSearch}
								style={{
									cursor: props.disabled
										? 'not-allowed'
										: 'initial',
									opacity: props.disabled ? 0.5 : 1
								}}
							/>
						</Button>
					</Tooltip>
				</>


			}
		},
		isMatchingROVisible && isRiskMode && {
			title: t("models.risk.fields.matching_opportunity"),
			dataIndex: 'matching_opportunity',
			key: 'matching_opportunity',
			width: 180,
			render: (text: string, record) => {
				// n-n : liens via la table risk-opening-links (source unique, E1).
				const linkedIds: string[] = (record.openingLinks || []).map((l: any) => l.openingId);
				const matchings = linkedIds
					.map((oid) => risks.find((c: any) => c.id === oid))
					.filter(Boolean);

				if (!matchings.length) return null;

				return <>
					{matchings.map((matching: any, index) => (
						<div key={index}>
							<span style={{ textOverflow: "ellipsis", maxWidth: "90%" }}>{matching.name}</span>
							<Button
								shape="circle"
								className='mr-2 mini-button'
								onClick={() => goToRisks('opportunities', matching.id)}
							>
								<FontAwesomeIcon
									icon={faSearch}
									style={{
										cursor: props.disabled
											? 'not-allowed'
											: 'initial',
										opacity: props.disabled ? 0.5 : 1
									}}
								/>
							</Button>
						</div>
					))}
				</>
			}
		},
		isMatchingROVisible && !isRiskMode && {
			title: t("models.risk.fields.matching_risks"),
			dataIndex: 'matching_risks',
			key: 'matching_risks',
			width: 180,
			render: (text: string, record) => {
				// n-n : risques liés à cette opening via la table de liens.
				const matching = risks.filter(
					(c: any) => (c.openingLinks || []).some((l: any) => l.openingId === record.id)
				)

				if (!matching.length) return null;

				return <>
					{matching.map((oneMatch, index) => {
						return <span key={index}>
							<span style={{ textOverflow: "ellipsis", maxWidth: "90%" }}>{oneMatch.name}</span>
							<Button
								shape="circle"
								className='mr-1 mini-button'
								onClick={() => goToRisks('risks', oneMatch.id)}
							>
								<FontAwesomeIcon
									icon={faSearch}
									style={{
										cursor: props.disabled
											? 'not-allowed'
											: 'initial',
										opacity: props.disabled ? 0.5 : 1
									}}
								/>
							</Button>
						</span>
					})}

				</>
			}
		},
		{
			title: '',
			dataIndex: 'select',
			key: 'select',
			width: 50,
			render: (text, record) => (

				selection?.id !== record.id ? <Tooltip title={t("actions.view")} placement='left'>
					<Button
						shape="circle"
						className={selection && selection.__typename === "Risk" ? "discreet" : ""}
						onClick={() => props.select(record)}
					>
						<FontAwesomeIcon
							icon={faChevronRight}
							style={{
								cursor: props.disabled
									? 'not-allowed'
									: 'initial'
							}}
						/>
					</Button>
				</Tooltip> : <Tooltip title={t("actions.close")} placement='left'>
					<Button
						shape="circle"
						onClick={() => props.deselect()}
					>
						<FontAwesomeIcon
							icon={faTimes}
							style={{
								cursor: props.disabled
									? 'not-allowed'
									: 'initial',
								opacity: props.disabled ? 0.5 : 1
							}}
						/>
					</Button>
				</Tooltip>

			)
		}
	].filter(Boolean);

	return (
		<Layout className="Risks pb-1" data-testid="risks-page">
			<SubHeaderPortal>
				{!onboardingActive && (
					<Tutorial
						type="risks"
						context={{ ...props }}
						executeAction={props.executeAction} projectId={params.id} />
				)}
			</SubHeaderPortal>

			<Layout.Content className="Body pt-1">

				<div className="pt-1">
					<MaskableComponent masked={uiContext.maskedZones['main']}>
						<Tabs
							type="card"
							className="m-auto w-100"
							style={{ maxWidth: '100%' }}
							activeKey={location.pathname?.includes("opportunities") ? "opportunities" : location.pathname?.includes("summary") ? "summary" : "risks"}
							onChange={(key) => {
								goToRisks(key)
							}}
							items={[
								{
									key: "risks",
									id: "risks",
									label: t("models.risk.name_plural"),
									children: (
										<div className="content pb-3 d-flex flex-column align-items-center">
											<div className="pr-1 w-100">
												<div style={{ height: "25px" }}>
													{
														location?.state?.riskFilter && (
															<Tag
																className="mb-3"
																color={'#FFFFFF00'}
																style={{
																	color: '#FFFFFF',
																	border: '0.5px solid #FFFFFF',
																}}
																onClick={() => goToRisks(isRiskMode ? 'risks' : 'opportunities')}
															>
																<FontAwesomeIcon icon={faTimes} style={{ marginRight: "0.5rem" }}></FontAwesomeIcon>
																{isRiskMode ? "Risk" : "Opportunity"}: {filteredElements[0]?.name || 'Unknown'}
															</Tag>
														)
													}
												</div>
												<div className="mb-3 mt-1 w-100 pr-2 pl-2" style={{ maxWidth: '100%' }}>
													<div
														className="scroll-container"
														style={{
															overflowY: 'auto',
															width: '100%',
															maxHeight: 'calc(100vh - 372px)',
														}}
													>
														<Table
															pagination={false}
															dataSource={sortBy(filteredElements, "name")}
															columns={columns}
															size="small"
															rowKey="id"
															className="mr-1"
															onRow={(record: any) => ({
																'data-testid': `risk-list-item-${record.id}`,
																onDoubleClick: () => setEditedItem(record),
															})}
														/>
													</div>
												</div>
											</div>
											<Flex justify="center" align="center">
												{uiContext.canEdit && <AddButton
													id="add-risk"
													data-testid="add-risk-button"
													popover={
														<>
															Add a risk
														</>
													}
													onClick={handleCreateRisk}
												></AddButton>}
											</Flex>
										</div>
									)
								},
								{
									id: "opportunities",
									key: "opportunities",
									label: t("models.risk.name_plural_positive"),
									children: (
										<div className="content pb-3 d-flex flex-column align-items-center">
											<div className="pr-1 w-100">
												<div style={{ height: "25px" }}>
													{
														location?.state?.riskFilter && (
															<Tag
																className="mb-3"
																color={'#FFFFFF00'}
																style={{
																	color: '#FFFFFF',
																	border: '0.5px solid #FFFFFF',
																}}
																onClick={() => goToRisks(isRiskMode ? 'risks' : 'opportunities')}
															>
																<FontAwesomeIcon icon={faTimes} style={{ marginRight: "0.5rem" }}></FontAwesomeIcon>
																{isRiskMode ? "Risk" : "Opportunity"}: {filteredElements[0]?.name || 'Unknown'}
															</Tag>
														)
													}
												</div>
												<div className="mb-3 mt-1 w-100 pr-2 pl-2" style={{ maxWidth: '100%' }}>
													<div
														className="scroll-container"
														style={{
															overflowY: 'auto',
															width: '100%',
															maxHeight: 'calc(100vh - 372px)',
														}}
													>
														<Table
															pagination={false}
															dataSource={sortBy(filteredElements, "name")}
															columns={columns}
															size="small"
															rowKey="id"
															className="mr-1"
															onRow={(record: any) => ({
																'data-testid': `risk-list-item-${record.id}`,
																onDoubleClick: () => setEditedItem(record),
															})}
														/>
													</div>
												</div>
											</div>
											<Flex justify="center" align="center">
												{uiContext.canEdit && <AddButton
													id="add-opportunity"
													data-testid="add-risk-button"
													popover={
														<>
															Add a risk
														</>
													}
													onClick={handleCreateRisk}
												></AddButton>}
											</Flex>
										</div>
									)
								},
								// Hide the "Visualisation" radar tab while in onboarding review:
								// the reviewer needs to focus on the list of items, not the
								// scatter view (which is a separate analytical concern).
								...(onboardingActive ? [] : [{
									id: "summary",
									key: "summary",
									label: t('pages.risks.visualization_tab'),
									children: (
										<div ref={vizContainerRef} style={{
											// Memes marges que la barre d'onglets : 1rem a gauche,
											// 1rem au-dela de la bande Filtres (35px) a droite, pour
											// que la viz ne deborde pas sous le sidemenu. La largeur
											// est l'espace restant (zone centrale) -> vizWidth mesure
											// cette zone et la viz s'y inscrit. Pierre #182.
											marginLeft: '1rem',
											marginRight: 'calc(35px + 1rem)',
											height: '700px'
										}}>
											<RiskScatterFullChart
												isRiskView={isRiskMode}
												projectId={params.id}
												recentCreations={props.recentCreations}
												onRiskClick={(risk) => {
													// Edit risk
													setEditedItem(risk);
												}}
												onUpdateItem={(updatedItem: any) => {
													// Update risk/opportunity position after drag & drop
													updateRisk(updatedItem.id, risks, props.scenario, {
														impact: updatedItem.impact,
														likelihood: updatedItem.likelihood
													});
												}}
												onCreate={(isOpportunity: boolean, position?: { impact: number; likelihood: number }) => {
													// Création depuis la viz : on réutilise le handler/modale
													// existant de la liste, en forçant la zone (Risques /
													// Ouvertures) et, si fournie, la position cliquée.
													handleCreateRisk({
														isOpportunity,
														...(position ? { impact: position.impact, likelihood: position.likelihood } : {})
													});
												}}
												likelihoods={indexedLikelihoods}
												impacts={indexedImpacts}
												style={{
													margin: 'auto',
													height: '700px',
													width: '100%',
													maxWidth: '1200px'
												}}
												width={vizWidth}
												resizing={vizResizing}
												height={700}
												onSelect={props.select}
												selection={selection}
												risks={filteredRisks}
												opportunities={filteredOpportunities}
												stakes={stakes}
												stakeholders={stakeholders}
												scenario={props.scenario}
												scenarios={scenarios}
												showAllLinks={areLinksVisible}
												showStakes={showStakes}
												showRisks={showRisks}
												showOpportunities={showOpportunities}
												showStakeholders={showStakeholders}
												riskCategories={riskCategories}
											/>
										</div>
									)
								}])
							]}
						/>
					</MaskableComponent>
				</div>

			</Layout.Content>

			<SideMenu defaultTab={selection?.__typename && selectableElementTypes.includes(selection?.__typename) ? "general" : "filters"} tabs={(() => {
				// Calcul du nombre d'éléments désactivés (filtrés selon le mode risques/opportunités)
				const disabledRisksFiltered = risks.filter(r => {
					const altered = getItemAlteredByScenario(r, props.scenario);
					if (isSummaryMode) {
						// En mode summary, on compte tous les désactivés
						return altered.enabled === false;
					}
					// Sinon, filtrer selon isRiskMode : risques si mode risque, opportunités si mode opportunités
					return altered.enabled === false && (isRiskMode ? !r.isOpportunity : !!r.isOpportunity);
				});
				const disabledCount = disabledRisksFiltered.length;

				return [
					(selection?.__typename && selectableElementTypes.includes(selection?.__typename)) && {
						id: 'general',
						title: t('pages.risks.tabs.general.title'),
					},
					!selection && {
						id: 'filters',
						title: t('pages.risks.tabs.filters.title')
					},
					!selection && {
						id: 'options',
						title: t('pages.risks.tabs.options.title')
					},
					selection?.__typename == "Risk" && props.features?.fishbone && {
						id: 'fishbone',
						title: t('pages.fishbone.title')
					},
					selection?.__typename == "Risk" && {
						id: 'stakeholders',
						title: t('pages.risks.tabs.stakeholders.title')
					},
					selection?.__typename === 'Risk' && {
						id: 'tags',
						title: t('pages.navigator.tabs.tags.title')
					},
					selection && {
						id: 'comments',
						title: t('pages.risks.tabs.comments.title')
					},
					!selection && {
						id: 'deactivated',
						title: `${t('pages.navigator.tabs.disabled.title')}${disabledCount > 0 ? ` (${disabledCount})` : ''}`
					},
					!selection && {
						id: 'bot',
						title: t('pages.navigator.tabs.bot.title')
					},
					{
						id: 'export',
						title: t('pages.navigator.tabs.export.title')
					}
				];
			})()}>
				{function (selectedTab, setSelectedTab: (t: string) => void) {

					if (selectedTab == "deactivated")
						return <DisabledTab
							scenario={props.scenario}
							risks={isSummaryMode ? risks : disabledRisksForTab}
							select={props.select}
							splitRisksAndOpportunities={isSummaryMode}
							onEnableItem={async (item) => {
								if (item.__typename === 'Risk') {
									await updateRisk(item.id, risks, props.scenario, { enabled: true });
								}
							}}
							onDeleteItem={(item) => {
								if (item.__typename === 'Risk') {
									askDeleteConfirmation(item.id, item.isOpportunity);
								}
							}}
						/>

					if (selectedTab == "export")
						return <ExportTab risks={risks} riskCategories={riskCategories} height={700 + 100}>

						</ExportTab>

					if (selectedTab == 'tags' && selection?.__typename === 'Risk') {
						return <ItemTagsTab
							allTags={allTags}
							item={selection}
							onClose={() => props.deselect()}
							onUpdateTags={async (item, tagIds) => {
								await updateRiskTaggings(item, tagIds);
								const kept = (item.taggings || []).filter((t: any) => tagIds.includes(t.tagId));
								const added = tagIds.filter((id: string) => !(item.taggings || []).find((t: any) => t.tagId === id)).map((id: string) => ({ tagId: id, objectId: item.id }));
								props.select({ ...item, taggings: [...kept, ...added] });
							}}
							onCreateTag={props.onCreateTag}
							areTagsVisible={props.preferences?.pages?.navigator?.tags ?? true}
							setPreferences={props.setPreferences}
							scenario={props.scenario}
						/>;
					}

					if (selectedTab == 'comments') {
						return <CommentsTab
							projectId={params.id}
							key={props.selection.id}
							type={props.selection?.__typename}
							selection={props.selection}
							pushModal={props.pushModal}
							popModal={props.popModal}
							scenario={props.scenario}
						>

						</CommentsTab>
					}

					return <>
						{selectedTab == "filters" && <FiltersTab
							setSearchTerm={props.setSearchTerm}
							searchTerm={props.searchTerm}

							displayRisks={props.displayRisks}

							categories={allCategories}
							categoryFilter={categoryFilter}
							setCategoryFilter={setCategoryFilter}

							impacts={impacts}
							setImpactFilter={setImpactFilter}
							impactFilter={impactFilter}

							likelihoods={likelihoods}
							setLikelihoodFilter={setLikelihoodFilter}
							likelihoodFilter={likelihoodFilter}

							tags={allTags}
							tagFilter={tagFilter}
							setTagFilter={setTagFilter}
							usedTags={usedTags}

							stakes={allStakes}
							stakeFilter={stakeFilter}
							setStakeFilter={setStakeFilter}

							stakeholders={allStakeholders}
							stakeholderFilter={stakeholderFilter}
							setStakeholderFilter={setStakeholderFilter}
						></FiltersTab>}

						{(!selection || (selection?.__typename != "Risk" && selection?.__typename != "Stake" && selection?.__typename != "Stakeholder")) && selectedTab == "options" && <OptionsTab
							displayColumns={!isSummaryMode}
							preferences={props.preferences?.pages?.risks}
							setPreferences={(key, value) => {
								props.setPreferences("pages", "risks." + key, value)
							}}
						></OptionsTab>}

						{(!selection || (selection?.__typename != "Risk" && selection?.__typename != "Stake" && selection?.__typename != "Stakeholder")) && selectedTab == "bot" && <>
							<BotTab
								projectId={params.id}
								messages={props.botThread?.messages || []}
								onResetThread={props.resetBotThread}
								onSendMessage={(message, callback) => props.addBotMessage(params.id, props.scenario?.id || null, "risks", message, callback)}
							>

							</BotTab></>}

						{(selection && selection?.__typename === "Risk" && selectedTab !== "stakeholders" && selectedTab !== "fishbone") && <RiskGeneralTab

							scenario={props.scenario}
							selection={selection ? getItemAlteredByScenario(selection, props.scenario) : null}
							onEdit={() => {
								setEditedItem(selection)
							}}
							key={"general" + selection.id}
							categories={allCategories}
							likelihoods={indexedLikelihoods}
							impacts={indexedImpacts}
							opportunities={filteredOpportunities}
							risks={risks.filter(r => !r.isOpportunity)}
							indicators={props.indicators}
							fishboneBranchs={props.fishboneBranchs}
							onClose={() => { props.deselect() }}
							onSetOpeningIds={(riskId, openingIds) => {
								updateRisk(
									riskId,
									risks,
									null,
									{ openingIds }
								);
							}}
							onCycleOpeningLink={async (risk, openingId) => {
								// Cycle 1->2->3->suppression du lien risk<->opening.
								// On relit le risque depuis la liste a jour (le cache
								// Apollo porte le strength courant), pas le snapshot
								// scenarise de `selection`.
								const carrier = risks.find((r) => r.id === risk?.id) || risk;
								await cycleOpeningLink(carrier, openingId);
							}}
							onDisable={async () => {
								await updateRisk(selection?.id, risks, props.scenario, { enabled: false });
								props.deselect();
							}}
							onDelete={() => {

								if (selection) {
									askDeleteConfirmation(selection?.id, selection?.isOpportunity);
								}
							}}

							stakes={props.stakes}
							createStakeImpact={async (a, b, c, d, e, f) => {
								let newImpact = await createStakeImpact(a, b, c, d, e, f);
								props.select({
									...selection,
									impactedStakes: [
										...selection.impactedStakes,
										newImpact
									]
								})
							}}
							updateImpactStrength={async (impact, strength) => {
								await updateStakeImpactStrength(impact, strength);

								props.select({
									...selection,
									impactedStakes: selection?.impactedStakes?.map(ii => {
										if (ii.id == impact.id) {
											return {
												...ii,
												strength
											}
										} else {
											return {
												...ii
											}

										}
									})
								})
							}}
							deleteStakeImpact={(id, riskId) => {
								deleteStakeImpact(id, riskId);
								props.select({
									...selection,
									impactedStakes: selection?.impactedStakes?.filter(ii => ii.id != id)

								})
							}}
						></RiskGeneralTab>}
						{(selection && selection?.__typename === "Risk" && selectedTab !== "tags") && (
							<div className="px-1 mt-1" onClick={() => setSelectedTab('tags')} style={{ cursor: 'pointer' }}>
								{(selection?.taggings?.length > 0) ? (
									(selection.taggings as any[]).map((tagging: any) => {
										const tag = allTags.find((t: any) => t.id === tagging.tagId);
										return tag ? (
											<Tag.CheckableTag key={tag.id} checked className="noselection">
												{tag.name}
											</Tag.CheckableTag>
										) : null;
									})
								) : (
									<span style={{ color: 'var(--ant-color-text-tertiary)', fontSize: '0.85rem' }}>Tags →</span>
								)}
							</div>
						)}

						{(selection && selection?.__typename === "Stake") && <StakeGeneralTab
							scenario={props.scenario}
							selection={selection}
							hideActions={true}
							risks={risks}
							indicators={props.indicators}
							key={"general" + selection.id}
							onClose={() => { props.deselect() }}

							stakes={props.stakes}
							createStakeImpact={async (riskId, stakeId) => {
								let newImpact = await createStakeImpact(riskId, stakeId);
								props.select({
									...selection
								})
							}}
							updateImpactStrength={async (impact, strength) => {
								await updateStakeImpactStrength(impact, strength);

								props.select({
									...selection
								})
							}}
							deleteStakeImpact={async (id, riskId) => {
								await deleteStakeImpact(id, riskId);
								props.select({
									...selection

								})
							}}
						></StakeGeneralTab>}

						{(selection && selection?.__typename === "Stakeholder") && <GeneralTab

							displayTitle={true}
							displayTabs={false}
							selection={selection}
							stakes={stakes}
							scenario={props.scenario}
							displayStakes={true}
							stakeholders={stakeholders}
							indexedStakeholders={indexedStakeholders}

							risks={risks}
							key={"general" + selection.id}
							categories={allCategories}
							onClose={() => { props.deselect() }}

							preferences={props.preferences?.pages?.risks}

							createStakeImpact={async (riskId, stakeId) => {
								let newImpact = await createStakeImpact(riskId, stakeId);
								props.select({
									...selection
								})
							}}
							updateImpactStrength={async (impact, strength) => {
								await updateStakeImpactStrength(impact, strength);

								props.select({
									...selection
								})
							}}
							deleteStakeImpact={async (id, riskId) => {
								await deleteStakeImpact(id, riskId);
								props.select({
									...selection

								})
							}}

							areStakesVisible={true}
							areIndicatorsVisible={false}
							areRisksVisible={true}
							areOpportunitiesVisible={true}
						></GeneralTab>}

						{(selection && selection?.__typename === "Risk" && selectedTab === "stakeholders") &&
							<StakeholderImpactsTab
								selection={selection}
								stakeholders={stakeholders}
								impactedFieldName="impactedRisks"
								targetIdFieldName="riskId"
								entityTypeLabel={selection.isOpportunity ? t('models.risk.name_positive') : t('models.risk.name')}
								createImpact={async (stakeholderId, riskId) => {
									await createRiskImpact(stakeholderId, riskId);
									props.select({ ...selection });
								}}
								updateImpactStrength={async (impact, strength) => {
									await updateRiskImpactStrength(impact, strength);
									props.select({ ...selection });
								}}
								deleteImpact={async (id, stakeholderId) => {
									await deleteRiskImpact(id, stakeholderId);
									props.select({ ...selection });
								}}
								onClose={() => props.deselect()}
							/>}

							{(selection && selection?.__typename === "Risk" && selectedTab === "fishbone") &&
								<FishboneLinksTab
									selection={selection}
									targetType="risk"
									fishboneBranchs={props.fishboneBranchs}
									scenario={props.scenario}
									select={props.select}
									onClose={() => props.deselect()}
								/>}

					</>
				}}
			</SideMenu>

			{editedItem && editedItem.__typename === "Risk" && (() => {
				// Appliquer les valeurs du scénario si un scénario est actif
				const alteredEditedItem = getItemAlteredByScenario(editedItem, props.scenario);
				return (
					<RiskEditionModal
						onHide={() => {
							setEditedItem(null);
						}}
						onDelete={() => {
							askDeleteConfirmation(editedItem.id, editedItem.isOpportunity);
							setEditedItem(null);
						}}
						onSave={async (values: Object) => {
							await updateRisk(
								editedItem.id,
								risks,
								props.scenario,
								values
							);
							setEditedItem(null);

						}}
						tags={props.tags}
						id={editedItem?.id}
						likelihoods={likelihoods || []}
						impacts={impacts || []}
						values={{
							// Champs baseline (non modifiables par scénario)
							name: editedItem?.name,
							reference: editedItem?.reference,
							description: editedItem?.description || '',
							riskCategoryId: editedItem?.riskCategoryId,
							isOpportunity: editedItem?.isOpportunity || false,
							openingIds: (editedItem?.openingLinks || []).map(l => l.openingId),
							taggings: editedItem?.taggings?.map(t => t.tagId) || [],
							evaluatedAt: editedItem?.evaluatedAt ? dayjs(editedItem?.evaluatedAt) : null,
							impactedStakes: (editedItem?.impactedStakes || []).map(ii => ii.stakeId),
							// Champs modifiables par scénario - utiliser les valeurs altérées
							impact: alteredEditedItem?.impact || 1,
							likelihood: alteredEditedItem?.likelihood || 1,
						}}
						riskCategories={riskCategories}
						risks={risks.filter(r => r.id != editedItem?.id)}
						stakes={stakes}
					></RiskEditionModal>
				);
			})()}

			{editedItem && editedItem.__typename === "Stake" && (() => {
				// Appliquer les valeurs du scénario si un scénario est actif
				const alteredEditedStake = getItemAlteredByScenario(editedItem, props.scenario);
				return (
					<StakeEditionModal
						onHide={() => {
							setEditedItem(null);
						}}
						onSave={async (values: Object) => {
							await updateStake(
								editedItem.id,
								stakes,
								props.scenario,
								values
							);
							setEditedItem(null);
						}}
						id={editedItem?.id}
						values={{
							// Champs baseline (non modifiables par scénario)
							name: editedItem?.name,
							description: editedItem?.description || '',
							reference: editedItem?.reference,
							evaluatedAt: editedItem?.evaluatedAt ? dayjs(editedItem?.evaluatedAt) : null,
							// Champs modifiables par scénario - utiliser les valeurs altérées
							weight: alteredEditedStake?.weight || 1,
						}}
					/>
				);
			})()}
		</Layout >
	);
};
